import { factoryCategoryShort, isFactoryRawMaterialCategory, resolveFactoryMaterialUnit } from '../constants/factoryStockCategories';
import { isLowStock, lowStockThreshold } from './lowStock';

const OPEN_ORDER_STATUSES = new Set([
  'pending',
  'pending_approval',
  'approved',
  'ordered',
  'partially_received',
]);

function normName(name) {
  return String(name || '')
    .trim()
    .toLowerCase();
}

function severityForItem(qty, limit) {
  if (qty <= 0) return 'critical';
  if (limit > 0 && qty < limit * 0.25) return 'urgent';
  return 'warning';
}

function healthBand(score) {
  if (score >= 85) return { key: 'good', label: 'Hali nzuri', tone: 'good' };
  if (score >= 65) return { key: 'watch', label: 'Fuatilia', tone: 'watch' };
  if (score >= 40) return { key: 'action', label: 'Hatua inahitajika', tone: 'action' };
  return { key: 'critical', label: 'Hatari — agiza haraka', tone: 'critical' };
}

function orderLines(orders) {
  const lines = [];
  for (const order of orders || []) {
    const status = String(order.status || '').toLowerCase();
    if (!OPEN_ORDER_STATUSES.has(status)) continue;
    const products = order.product_requests || order.productRequests || [];
    for (const line of products) {
      lines.push({
        productName: line.product_name || line.productName || '',
        poNumber: order.po_number || `#${order.id}`,
        status,
      });
    }
  }
  return lines;
}

function findOpenOrderForProduct(productName, openLines) {
  const key = normName(productName);
  return openLines.find((l) => normName(l.productName) === key) || null;
}

/**
 * @param {{ products: object[], factoryMode?: boolean, orders?: object[] }} input
 */
export function buildLowStockInsights({ products, factoryMode = false, orders = [] }) {
  const list = Array.isArray(products) ? products : [];
  const inventory = factoryMode
    ? list.filter((p) => isFactoryRawMaterialCategory(p.category))
    : list;

  const tracked = inventory.filter((p) => {
    const lim = Number(p.lowStockLimit);
    return Number.isFinite(lim) && lim > 0;
  });

  const alerts = inventory
    .filter(isLowStock)
    .map((p) => {
      const qty = Number(p.quantity) || 0;
      const limit = lowStockThreshold(p);
      const unit = resolveFactoryMaterialUnit(p);
      const shortfall = Math.max(0, limit - qty);
      const fillPct = limit > 0 ? Math.min(100, Math.round((qty / limit) * 100)) : 0;
      const openLines = orderLines(orders);
      const onOrder = findOpenOrderForProduct(p.name, openLines);
      return {
        id: p.id,
        name: p.name,
        category: p.category || '',
        categoryShort: factoryMode ? factoryCategoryShort(p.category) : p.category || '—',
        quantity: qty,
        limit,
        unit,
        shortfall,
        fillPct,
        severity: severityForItem(qty, limit),
        hasCustomLimit: Number(p.lowStockLimit) > 0,
        onOrder,
      };
    })
    .sort((a, b) => {
      const rank = { critical: 0, urgent: 1, warning: 2 };
      const d = rank[a.severity] - rank[b.severity];
      return d !== 0 ? d : a.quantity - b.quantity;
    });

  const totalItems = inventory.length;
  const healthyCount = totalItems - alerts.length;
  const outOfStock = alerts.filter((a) => a.quantity <= 0).length;
  const urgentCount = alerts.filter((a) => a.severity === 'urgent').length;
  const onOrderCount = alerts.filter((a) => a.onOrder).length;
  const noLimitCount = inventory.filter((p) => !(Number(p.lowStockLimit) > 0)).length;

  const healthScore =
    totalItems === 0 ? 100 : Math.round((healthyCount / totalItems) * 100);
  const band = healthBand(healthScore);

  const categoryMap = new Map();
  for (const a of alerts) {
    const key = a.categoryShort || 'Other';
    categoryMap.set(key, (categoryMap.get(key) || 0) + 1);
  }
  const categoryBreakdown = [...categoryMap.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);

  const recommendations = [];

  if (alerts.length === 0) {
    recommendations.push({
      tone: 'good',
      text: factoryMode
        ? 'Hali nzuri — malighafi yako yako juu ya kiwango cha chini. Endelea kufuatilia kila wiki.'
        : 'Stock levels look healthy. No items are below their minimum threshold.',
    });
  } else {
    if (outOfStock > 0) {
      const names = alerts
        .filter((a) => a.quantity <= 0)
        .slice(0, 3)
        .map((a) => a.name)
        .join(', ');
      recommendations.push({
        tone: 'critical',
        text: `Agiza mara moja: ${outOfStock} material${outOfStock !== 1 ? 's' : ''} zimeisha kabisa (${names}${outOfStock > 3 ? '…' : ''}).`,
      });
    }

    if (onOrderCount > 0) {
      recommendations.push({
        tone: 'info',
        text: `${onOrderCount} low-stock item${onOrderCount !== 1 ? 's' : ''} tayari iko kwenye order — fuatilia Tracking status na pokea delivery mapema.`,
      });
    }

    const needOrder = alerts.filter((a) => !a.onOrder);
    if (needOrder.length > 0) {
      const top = needOrder.slice(0, 3).map((a) => a.name).join(', ');
      recommendations.push({
        tone: 'action',
        text: `Tengeneza material order kwa: ${top}${needOrder.length > 3 ? ` na ${needOrder.length - 3} zingine` : ''}.`,
      });
    }

    if (urgentCount > 0 && outOfStock === 0) {
      recommendations.push({
        tone: 'warn',
        text: `${urgentCount} material${urgentCount !== 1 ? 's' : ''} iko chini sana — agiza kabla hazijaisha.`,
      });
    }

    if (factoryMode && noLimitCount > 0) {
      recommendations.push({
        tone: 'info',
        text: `Weka low stock alert kwenye ${noLimitCount} material${noLimitCount !== 1 ? 's' : ''} ili upewe taarifa mapema (sasa inatumia default 5).`,
      });
    }

    if (tracked.length > 0 && alerts.length / Math.max(tracked.length, 1) > 0.5) {
      recommendations.push({
        tone: 'warn',
        text: 'Zaidi ya nusu ya malighafi zako ziko chini ya kiwango — uzalishaji unaweza kusimama ikiwa hutaagiza.',
      });
    }
  }

  const summary =
    alerts.length === 0
      ? factoryMode
        ? 'Stock yako ya malighafi iko salama. Hakuna hatua ya haraka.'
        : 'All monitored items are above their low-stock limits.'
      : outOfStock > 0
        ? `Kuna ${alerts.length} tahadhari — ${outOfStock} zimeisha kabisa. Agiza na pokea delivery haraka iwezekanavyo.`
        : `Kuna ${alerts.length} tahadhari. Agiza materials zinazohitajika na fuatilia orders zilizopo.`;

  return {
    alerts,
    stats: {
      totalItems,
      healthyCount,
      alertCount: alerts.length,
      outOfStock,
      urgentCount,
      onOrderCount,
      noLimitCount,
      healthScore,
      band,
    },
    categoryBreakdown,
    recommendations,
    summary,
  };
}
